import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { ConfirmModal } from "@solomon-apps/shared/src/components/ui/confirm-modal";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-apps/shared/src/components/ui/tooltip";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { TooltipIcon } from "@solomon-labs/design/components/icons";
import { Input } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useEffect, useState } from "react";

import { useClientWalletVerification } from "@/hooks/useClientWalletVerification";
import { useSquadsMultisig } from "@/hooks/useSquadsMultisig";
import type { WalletView } from "@/services/clientWallets";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useReportOnboardingDraft } from "../onboardingDraftContext";
import { useWalletDraftState } from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { FormPrimaryAction } from "../panel/FormPrimaryAction";
import type { NextStepTarget } from "../types";

// Participant Account records are the sole authority for verified wallets. The
// wallet_declarations step's status is flipped to Complete inside the
// wallet-verification ceremony's transaction
// (`OnboardingStepService.markStepCompleteForClientOrgInTx`). No
// snapshotting of entries[] from the FE — the BE re-derives the wallet
// list from Participant Account records at read time.

/**
 * Wallet-declaration form. One submission per wallet.
 *
 * The user pastes (or auto-fills from their connected wallet) a Solana
 * address; the backend determines whether it's an EOA or a Squads V4
 * vault via an on-curve check and lazy on-chain resolution. The
 * frontend shows read-only multisig structure when it resolves, then
 * collects a single signed-message proof bound to a server-issued
 * nonce. On success, the persisted `WalletView` is handed back to the
 * hub via `onSubmit` so it can update its local label/count cache; the
 * source-of-truth is the server (re-hydrated on mount in
 * `OnboardingPanel`).
 *
 * The pasted address is what the user signs and what the message
 * carries; server-side canonicalization to the multisig PDA (for
 * Squads) happens after signature verification — the frontend never
 * canonicalizes. See claude-docs/onboarding/squads-wallet-verification.md.
 */
export function WalletForm({
    slotKey,
    slotTitle,
    wallet,
    label,
    onLabelChange,
    onSubmit,
    onDelete,
    nextStep,
    onContinue
}: {
    /** Stable row id for the hub-persisted address draft (e.g. `wallet-0`). */
    slotKey: string;
    slotTitle: string;
    /** Persisted Participant Account wallet data. A non-null value means
     *  verification and save have completed for this slot. */
    wallet: WalletView | null;
    /** Controlled — lives in the hub so the typed label can feed back
     *  into the synthetic slot title in the left list. */
    label: string;
    onLabelChange: (next: string) => void;
    /** Persisted catalog row returned by the server. */
    onSubmit: (wallet: WalletView) => void;
    onDelete?: () => void;
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    // The typed-but-unverified address lives in the hub-scoped form-state
    // context keyed by slotKey, so it survives navigating away from this
    // wallet's slot and back before verification completes.
    const [draft, setDraft] = useWalletDraftState(slotKey);
    const address = wallet?.address ?? draft.address;
    const [deleteOpen, setDeleteOpen] = useState(false);
    const walletVerification = useClientWalletVerification();
    const {
        connected,
        connectedAddress,
        error: verifyError,
        isVerifying
    } = walletVerification;
    const isVerified = wallet !== null;

    // Auto-fill the address from the connected wallet on first connect.
    // User can still override by typing; we only auto-fill when the input
    // is empty so we never clobber a paste.
    useEffect(() => {
        if (!isVerified && connectedAddress && !draft.address) {
            setDraft({ address: connectedAddress });
        }
    }, [connectedAddress, draft.address, isVerified, setDraft]);

    // Live preflight against the backend orchestrator. Drives the
    // read-only multisig-info display when the address resolves to a
    // Squads V4 vault. `idle` for empty input; `not-found` for on-curve
    // EOAs (we treat those as a valid EOA path on Verify).
    const multisigStatus = useSquadsMultisig(address, connectedAddress);

    const addressEntered = address.trim().length > 0;
    const hasDraft = !isVerified && (addressEntered || label.trim().length > 0);
    useReportOnboardingDraft("wallet-primary", hasDraft);
    // The button is dual-purpose: when the Solana wallet isn't connected
    // yet it acts as a "Connect wallet" CTA that opens the wallet modal;
    // once connected it runs the verify ceremony. We must NOT disable
    // when `!connected` — that would make the connect-CTA unreachable.
    // Preflight `loading` only blocks the *verify* action; while not
    // connected the user should be free to open the modal in parallel.
    const verifyDisabled =
        !addressEntered ||
        isVerifying ||
        (connected && multisigStatus.status === "loading");

    async function handleVerify(): Promise<void> {
        const savedWallet = await walletVerification.verify(address, label);
        if (savedWallet) {
            onSubmit(savedWallet);
            if (context) void context.refetch();
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title={slotTitle}
                actions={
                    onDelete && !isVerified ? (
                        <Button
                            variant="sunken"
                            interaction="lifted"
                            outline
                            size="sm"
                            onClick={() => setDeleteOpen(true)}
                        >
                            Delete
                        </Button>
                    ) : undefined
                }
                className="pb-4 border-b border-line-base"
            />
            {onDelete && !isVerified && (
                <ConfirmModal
                    open={deleteOpen}
                    onOpenChange={setDeleteOpen}
                    titleVariant="h2"
                    title={`Delete ${slotTitle}?`}
                    description="This removes the wallet declaration and clears any in-progress fields for it. You can add a replacement at any time."
                    confirmLabel="Delete"
                    destructive
                    onConfirm={onDelete}
                />
            )}

            <FormCard
                title="Wallet Label (Optional)"
                hint="Use this to name your wallet to make it easier to recognize later."
            >
                <Input
                    aria-label="Wallet Label (Optional)"
                    value={label}
                    onChange={(e) => onLabelChange(e.target.value)}
                    readOnly={isVerified}
                    placeholder="e.g. Treasury"
                />
            </FormCard>

            <FormCard title="Wallet Address">
                <Input
                    aria-label="Wallet Address"
                    value={address}
                    readOnly={isVerified}
                    onChange={(e) => {
                        setDraft({ address: e.target.value });
                        walletVerification.clearError();
                    }}
                    placeholder="Solana address"
                />

                {/* Read-only multisig info — only when preflight resolves */}
                {multisigStatus.status === "success" && (
                    <div className="pt-3">
                        <Text
                            variant="label"
                            className="block text-foreground-neutral"
                        >
                            Squads V4 multisig
                        </Text>
                        <Text variant="small" className="block leading-snug">
                            {multisigStatus.multisig.threshold} of{" "}
                            {multisigStatus.multisig.memberCount} signers
                        </Text>
                    </div>
                )}
                {multisigStatus.status === "loading" && (
                    <div className="pt-3">
                        <Text variant="small" className="text-foreground-muted">
                            Looking up multisig structure…
                        </Text>
                    </div>
                )}

                {/* Verify CTA + signed-state + error */}
                <TooltipProvider>
                    <div className="pt-6 flex flex-col gap-2">
                        {isVerified ? (
                            <div className="flex items-center gap-2">
                                <IndicationUnit
                                    variant="status"
                                    state="success"
                                    size={6}
                                />
                                <Text
                                    variant="small"
                                    className="leading-snug text-foreground-base"
                                >
                                    <span className="font-bold">
                                        Wallet verified
                                    </span>{" "}
                                    via signed message
                                </Text>
                            </div>
                        ) : (
                            <Tooltip>
                                <TooltipTrigger
                                    render={<div className="w-full" />}
                                >
                                    <Button
                                        variant="raised"
                                        interaction="light-bar"
                                        size="sm"
                                        fullWidth
                                        disabled={verifyDisabled}
                                        onClick={handleVerify}
                                        trailing={
                                            <TooltipIcon className="size-4" />
                                        }
                                    >
                                        {isVerifying
                                            ? "Waiting for signature…"
                                            : connected
                                              ? "Verify Wallet"
                                              : "Connect Wallet to Verify"}
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                    We&apos;ll ask your connected wallet to sign
                                    a server-issued challenge proving control.
                                    For a Squads multisig, your connected wallet
                                    must be a Vote member.
                                </TooltipContent>
                            </Tooltip>
                        )}

                        {verifyError && (
                            <Text
                                variant="small"
                                className="block leading-snug text-error-base"
                            >
                                {verifyError}
                            </Text>
                        )}
                    </div>
                </TooltipProvider>
            </FormCard>

            {isVerified && (
                <FormPrimaryAction
                    submittable
                    submitting={false}
                    isSavedClean
                    nextStep={nextStep ?? null}
                    onContinue={onContinue}
                    onSave={() => undefined}
                />
            )}
        </motion.div>
    );
}
